import { useState, useEffect } from 'react';
import type { Source, CreateSourceRequest, SourceType } from '@claude-code-insights/shared';
import { SOURCE_TYPES } from '@claude-code-insights/shared';
import { fetchSources, createSource, updateSource, deleteSource } from '../lib/api';

export function SourcesView() {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  // Add form state
  const [newType, setNewType] = useState<SourceType>('hackernews');
  const [newName, setNewName] = useState('');
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newFilters, setNewFilters] = useState('');

  async function loadSources() {
    setLoading(true);
    try {
      const result = await fetchSources();
      setSources(result.items);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSources();
  }, []);

  async function handleAddSource() {
    const data: CreateSourceRequest = {
      type: newType,
      name: newName,
      identifier: newIdentifier,
      filters: newFilters
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean),
    };
    await createSource(data);
    setShowAdd(false);
    setNewName('');
    setNewIdentifier('');
    setNewFilters('');
    await loadSources();
  }

  async function handleToggleSource(source: Source) {
    await updateSource(source.sourceId, { enabled: !source.enabled });
    await loadSources();
  }

  async function handleDeleteSource(sourceId: string) {
    if (!confirm('Delete this source?')) return;
    await deleteSource(sourceId);
    await loadSources();
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Data Sources</h2>
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary text-sm">
          + Add Source
        </button>
      </div>

      {/* Add source form */}
      {showAdd && (
        <div className="card mb-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                Type
              </label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as SourceType)}
                className="input w-full text-sm"
              >
                {SOURCE_TYPES.filter((t) => t !== 'telegram').map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-obsidian-text-faint block mb-1">
                Name
              </label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g., Claude AI Subreddit"
                className="input w-full text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              Identifier (username, subreddit, RSS URL, etc.)
            </label>
            <input
              value={newIdentifier}
              onChange={(e) => setNewIdentifier(e.target.value)}
              placeholder="e.g., ClaudeAI"
              className="input w-full text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-obsidian-text-faint block mb-1">
              Keyword filters (comma-separated)
            </label>
            <input
              value={newFilters}
              onChange={(e) => setNewFilters(e.target.value)}
              placeholder="e.g., claude code, mcp server, claude skills"
              className="input w-full text-sm"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="btn-ghost text-sm">
              Cancel
            </button>
            <button
              onClick={handleAddSource}
              disabled={!newName || !newIdentifier}
              className="btn-primary text-sm disabled:opacity-50"
            >
              Add Source
            </button>
          </div>
        </div>
      )}

      {/* Sources list */}
      {loading ? (
        <div className="py-8 text-center text-obsidian-text-muted">Loading...</div>
      ) : sources.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-obsidian-text-muted text-sm">No sources configured</p>
          <p className="text-obsidian-text-faint text-xs mt-2">
            Add a data source to start collecting insights
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((source) => (
            <div key={source.sourceId} className="card flex items-center gap-4">
              {/* Enable toggle */}
              <button
                onClick={() => handleToggleSource(source)}
                className={`w-10 h-5 rounded-full transition-colors relative ${
                  source.enabled ? 'bg-obsidian-success' : 'bg-obsidian-surface'
                }`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    source.enabled ? 'left-5' : 'left-0.5'
                  }`}
                />
              </button>

              {/* Source info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium">{source.name}</h3>
                  <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint text-xs">
                    {source.type}
                  </span>
                </div>
                <p className="text-xs text-obsidian-text-muted mt-0.5">
                  {source.identifier}
                  {source.filters.length > 0 && (
                    <span className="ml-2 text-obsidian-text-faint">
                      Filters: {source.filters.join(', ')}
                    </span>
                  )}
                </p>
              </div>

              {/* Last polled */}
              <div className="text-right">
                {source.lastPolledAt ? (
                  <p className="text-xs text-obsidian-text-faint">
                    Last polled: {new Date(source.lastPolledAt).toLocaleString()}
                  </p>
                ) : (
                  <p className="text-xs text-obsidian-text-faint">Never polled</p>
                )}
                {source.lastError && (
                  <p className="text-xs text-obsidian-error mt-0.5 truncate max-w-48">
                    {source.lastError}
                  </p>
                )}
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDeleteSource(source.sourceId)}
                className="text-obsidian-text-faint hover:text-obsidian-error transition-colors text-sm"
                title="Delete source"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
